import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { Globe, CreditCard, Mail, Bell, Shield } from 'lucide-react';

export default function SettingsPage() {
  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div>
        <h1 className="text-2xl font-bold">Paramètres</h1>
        <p className="text-muted-foreground">Configurez votre boutique</p>
      </div>

      <div className="grid gap-6">
        {/* General */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Général
            </CardTitle>
            <CardDescription>
              Paramètres généraux de la boutique
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="storeName">Nom de la boutique</Label>
                <Input id="storeName" defaultValue="VyaStore" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="storeEmail">Email de contact</Label>
                <Input id="storeEmail" defaultValue="contact@vyastore.com" className="mt-1" />
              </div>
            </div>
            
            <Separator />
            
            <div>
              <Label className="text-base font-medium">Langues</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Langues disponibles sur la boutique
              </p>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <Switch defaultChecked />
                  <span>🇫🇷 Français</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch defaultChecked />
                  <span>🇬🇧 English</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Paiements
            </CardTitle>
            <CardDescription>
              Configuration des moyens de paiement
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Viva Wallet</p>
                  <p className="text-sm text-muted-foreground">Paiement par carte bancaire</p>
                </div>
              </div>
              <Switch defaultChecked />
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-xl">🅿️</span>
                </div>
                <div>
                  <p className="font-medium">PayPal</p>
                  <p className="text-sm text-muted-foreground">Paiement via PayPal</p>
                </div>
              </div>
              <Switch defaultChecked />
            </div>

            <p className="text-sm text-muted-foreground">
              💡 Les clés API sont configurées dans les variables d'environnement.
            </p>
          </CardContent>
        </Card>

        {/* Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Emails
            </CardTitle>
            <CardDescription>
              Configuration de l'envoi d'emails
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtpHost">Serveur SMTP</Label>
                <Input id="smtpHost" defaultValue="smtp.gmail.com" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="smtpPort">Port</Label>
                <Input id="smtpPort" defaultValue="587" className="mt-1" />
              </div>
            </div>
            
            <div>
              <Label htmlFor="senderEmail">Email expéditeur</Label>
              <Input id="senderEmail" defaultValue="noreply@vyastore.com" className="mt-1" />
            </div>

            <Button variant="outline">
              Tester l'envoi
            </Button>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications
            </CardTitle>
            <CardDescription>
              Gérez les notifications admin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Nouvelle commande</p>
                <p className="text-sm text-muted-foreground">Recevoir un email à chaque nouvelle commande</p>
              </div>
              <Switch defaultChecked />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Nouveau lead</p>
                <p className="text-sm text-muted-foreground">Notification pour chaque nouveau lead</p>
              </div>
              <Switch />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Stock faible</p>
                <p className="text-sm text-muted-foreground">Alerte quand un produit est en rupture</p>
              </div>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Sécurité
            </CardTitle>
            <CardDescription>
              Paramètres de sécurité
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Double authentification</p>
                <p className="text-sm text-muted-foreground">Activer la 2FA pour les admins</p>
              </div>
              <Switch />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Rate limiting</p>
                <p className="text-sm text-muted-foreground">Limiter les requêtes API</p>
              </div>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button>Enregistrer les modifications</Button>
        </div>
      </div>
    </div>
  );
}
